<template>
  <div class="harvest-schedule-price-approval-view w-100 pa-4">
    <!-- Header -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-icon color="primary" large class="mr-3">mdi-cash-check</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Aprobación de Precios de Cosecha
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Control y autorización de sobreprecios en programaciones de cosecha frente a la matriz de precios configurada.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- Grilla Principal S-CRUD -->
    <s-crud
      ref="scrudPriceApproval"
      title="Programaciones de Cosecha con Control de Precios"
      :config="config"
      :filter="filter"
      :refreshTable="refreshTableToggle"
      search-input
      height="60vh"
    >
      <template v-slot:filter>
        <v-container fluid class="pa-2">
          <v-row dense align="center">
            <v-col cols="12" sm="6" md="3" class="s-col-form">
              <s-date
                v-model="filter.DateBegin"
                label="Fecha Inicio Cosecha"
                clearable
              ></s-date>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="s-col-form">
              <s-date
                v-model="filter.DateEnd"
                label="Fecha Fin Cosecha"
                clearable
              ></s-date>
            </v-col>
            <v-col cols="12" sm="6" md="3" class="s-col-form">
              <s-select
                v-model="filter.TypeApprovalStatus"
                :items="statusOptions"
                label="Estado Aprobación"
                clearable
                dense
              ></s-select>
            </v-col>
          </v-row>
        </v-container>
      </template>

      <!-- Slot Acciones -->
      <template v-slot:actions="{ row }">
        <div class="d-flex align-center justify-center">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click="openEvaluationModal(row)"
              >
                <v-icon small>mdi-shield-check-outline</v-icon>
              </v-btn>
            </template>
            <span>Evaluar / Gestionar Precios</span>
          </v-tooltip>
        </div>
      </template>

      <!-- Slot Estado -->
      <template v-slot:TypeApprovalStatus="{ row }">
        <v-chip
          small
          :color="getStatusChipColor(row)"
          :class="getStatusChipClass(row)"
          class="font-weight-black px-2 status-chip"
          :style="getStatusChipStyle(row)"
        >
          <v-icon x-small left :color="getStatusIconColor(row)">{{ getStatusIcon(row) }}</v-icon>
          <span>{{ getRowStatusName(row) }}</span>
        </v-chip>
      </template>

      <!-- Slot N° Prog -->
      <template v-slot:HvsID="{ row }">
        <span class="font-weight-bold grey--text text--darken-4">
          #{{ row.HvsID }}
        </span>
      </template>

      <!-- Slot Fecha -->
      <template v-slot:Date="{ row }">
        <span>{{ formatDateDMY(row.Date) }}</span>
      </template>

      <!-- Slot Fundo -->
      <template v-slot:FagName="{ row }">
        <span>{{ row.FagName || '-' }}</span>
      </template>

      <!-- Slot Productor -->
      <template v-slot:PrdCardName="{ row }">
        <span class="font-weight-medium grey--text text--darken-4">{{ row.PrdCardName || '-' }}</span>
      </template>

      <!-- Slots dinámicos de columnas de precio después de PrdCardName -->
      <template v-for="col in dynamicColumns" v-slot:[col]="{ row }">
        <div :key="col" class="d-flex align-center justify-end w-100">
          <!-- Si no hay valor registrado -->
          <template v-if="row[col] === null || row[col] === undefined || row[col] === ''">
            <span class="grey--text text--lighten-1 font-weight-medium">-</span>
          </template>

          <!-- CASO 1: Sobreprecio detectado (Precio Acordado > Precio Configurado) -->
          <template v-else-if="isOverprice(row, col)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  color="red lighten-5"
                  class="overprice-chip red--text text--darken-4 font-weight-black px-2 cursor-pointer"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small color="error" class="mr-1">mdi-arrow-up-bold</v-icon>
                  <span>{{ formatPrice(row[col]) }}</span>
                  <span class="ml-1 text-caption font-weight-black red--text text--darken-2">
                    (+{{ formatDecimal(getOverpriceDiff(row, col)) }})
                  </span>
                </v-chip>
              </template>
              <span>
                <strong>¡Sobreprecio detectado!</strong><br />
                Pactado: {{ formatPrice(row[col]) }}<br />
                Matriz: {{ formatPrice(row[getConfiguredColName(col)]) }}<br />
                Excede por: +S/ {{ formatDecimal(getOverpriceDiff(row, col)) }}
              </span>
            </v-tooltip>
          </template>

          <!-- CASO 2: Precio Acordado menor que matriz (Favorable / Ahorro) -->
          <template v-else-if="isUnderprice(row, col)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  color="green lighten-5"
                  class="underprice-chip green--text text--darken-4 font-weight-black px-2 cursor-pointer"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small color="success" class="mr-1">mdi-check</v-icon>
                  <span>{{ formatPrice(row[col]) }}</span>
                </v-chip>
              </template>
              <span>
                <strong>Precio Conforme</strong><br />
                Pactado: {{ formatPrice(row[col]) }}<br />
                Matriz: {{ formatPrice(row[getConfiguredColName(col)]) }} (Dentro de matriz)
              </span>
            </v-tooltip>
          </template>

          <!-- CASO 3: Precio Acordado igual al configurado -->
          <template v-else-if="isEqualPrice(row, col)">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  color="blue-grey lighten-5"
                  class="equalprice-chip blue-grey--text text--darken-4 font-weight-black px-2 cursor-pointer"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small color="blue-grey darken-1" class="mr-1">mdi-check-circle-outline</v-icon>
                  <span>{{ formatPrice(row[col]) }}</span>
                </v-chip>
              </template>
              <span>
                <strong>Precio Conforme</strong><br />
                Pactado igual a Matriz: {{ formatPrice(row[col]) }}
              </span>
            </v-tooltip>
          </template>

          <!-- CASO 4: Precio Configurado (Matriz de referencia) -->
          <template v-else-if="col.includes('Precio Configurado')">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-chip
                  small
                  color="grey lighten-4"
                  class="config-price-chip grey--text text--darken-3 font-weight-bold px-2 cursor-pointer"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon x-small color="grey darken-2" class="mr-1">mdi-tune-variant</v-icon>
                  <span>{{ formatPrice(row[col]) }}</span>
                </v-chip>
              </template>
              <span>Precio Base Matriz: {{ formatPrice(row[col]) }}</span>
            </v-tooltip>
          </template>

          <!-- Default fallback -->
          <template v-else>
            <span class="grey--text text--darken-3 font-weight-medium">
              {{ formatPrice(row[col]) }}
            </span>
          </template>
        </div>
      </template>
    </s-crud>

    <!-- Modal de Evaluación y Detalle de Precios -->
    <v-dialog v-model="dialogEvaluation" max-width="850px" persistent>
      <v-card class="rounded-xl border-light" outlined elevation="0">
        <v-card-title class="d-flex align-center justify-space-between py-3 px-5 border-light-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-clipboard-check-outline</v-icon>
            <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
              Evaluación de Precios — Prog. #{{ selectedRow.HvsID }}
            </span>
          </div>
          <v-btn icon small @click="closeEvaluationModal">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-5">
          <v-card class="pa-4 mb-4 rounded-lg border-light bg-light" outlined elevation="0">
            <v-row dense>
              <v-col cols="12" sm="4">
                <div class="text-caption grey--text text--darken-2 font-weight-bold">PRODUCTOR</div>
                <div class="text-body-2 font-weight-black grey--text text--darken-4">{{ selectedRow.PrdCardName || '-' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="text-caption grey--text text--darken-2 font-weight-bold">FUNDO</div>
                <div class="text-body-2 font-weight-black grey--text text--darken-4">{{ selectedRow.FagName || '-' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="text-caption grey--text text--darken-2 font-weight-bold">FECHA DE COSECHA</div>
                <div class="text-body-2 font-weight-black grey--text text--darken-4">{{ formatDateDMY(selectedRow.Date) }}</div>
              </v-col>
            </v-row>
          </v-card>

          <!-- Tabla de Precios -->
          <v-data-table
            :headers="priceHeaders"
            :items="priceDetails"
            :loading="loadingPrices"
            dense
            hide-default-footer
            :items-per-page="-1"
            class="border-rounded border-light elevation-0 mb-4"
          >
            <template v-slot:item.Amount="{ item }">
              <span class="font-weight-bold">S/ {{ formatDecimal(item.Amount) }}</span>
            </template>
            <template v-slot:item.ConfiguredAmount="{ item }">
              <span class="grey--text text--darken-2">S/ {{ formatDecimal(item.ConfiguredAmount) }}</span>
            </template>
            <template v-slot:item.Difference="{ item }">
              <span :class="computeDifference(item) > 0 ? 'error--text font-weight-black' : 'success--text'">
                S/ {{ formatDecimal(computeDifference(item)) }}
              </span>
            </template>
            <template v-slot:item.Condition="{ item }">
              <v-chip x-small :color="computeDifference(item) > 0 ? 'error' : 'success'" dark class="font-weight-bold">
                {{ computeDifference(item) > 0 ? 'Sobreprecio' : 'Conforme' }}
              </v-chip>
            </template>
          </v-data-table>

          <s-textarea
            v-model="approvalForm.Observation"
            label="Observación / Justificación"
            placeholder="Ingrese observación..."
            :rows="3"
            outlined
            dense
          ></s-textarea>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="py-3 px-5 d-flex justify-space-between flex-wrap">
          <v-btn text small color="grey darken-2" class="text-none font-weight-bold" @click="closeEvaluationModal">
            Cancelar
          </v-btn>

          <div class="d-flex align-center" style="gap: 8px;">
            <v-btn
              color="error"
              class="rounded-lg text-none font-weight-bold px-4"
              small
              elevation="0"
              :loading="savingApproval"
              @click="submitApproval(2)"
            >
              Rechazar Sobreprecio
            </v-btn>

            <v-btn
              color="primary"
              class="rounded-lg text-none font-weight-bold px-4"
              small
              elevation="0"
              :loading="savingApproval"
              @click="submitApproval(1)"
            >
              Aprobar Programación
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import HarvestSchedulePriceApprovalService from "@/services/Technicalassistance/HarvestSchedulePriceApprovalService";

export default {
  name: "HarvestSchedulePriceApproval",

  data() {
    const vm = this;
    const interceptedService = {
      ...HarvestSchedulePriceApprovalService,
      pagination(parameters, requestID, cancelToken) {
        return HarvestSchedulePriceApprovalService.pagination(parameters, requestID, cancelToken).then((response) => {
          vm.handlePaginationResponse(response);
          return response;
        });
      },
    };

    return {
      refreshTableToggle: false,
      savingApproval: false,
      loadingPrices: false,
      dialogEvaluation: false,

      selectedRow: {},
      dynamicColumns: [],

      statusOptions: [
        { text: "Aprobado", value: 1 },
        { text: "Rechazado", value: 2 },
      ],

      filter: {
        DateBegin: null,
        DateEnd: null,
        TypeApprovalStatus: null,
        SoloSinAprobacion: null
      },

      config: {
        title: "Aprobación de Precios de Cosecha",
        service: interceptedService,
        model: {
          HsaID: "ID",
          TypeApprovalStatus: "int",
          HvsID: "int",
          Date: "date",
          FagName: "string",
          PrdCardName: "string",
        },
        headers: [
          { text: "Acciones", value: "actions", sortable: false, width: "80px", align: "center" },
          { text: "Estado", value: "TypeApprovalStatus", width: "135px", align: "center", sortable: true },
          { text: "N° Prog.", value: "HvsID", width: "95px", align: "center", sortable: true },
          { text: "Fecha Cosecha", value: "Date", width: "125px", align: "center", sortable: true },
          { text: "Fundo", value: "FagName", width: "160px", sortable: true },
          { text: "Productor", value: "PrdCardName", width: "220px", sortable: true },
        ],
      },

      priceHeaders: [
        { text: "Categoría / Calibre", value: "CtgName", sortable: false },
        { text: "Moneda", value: "TypeCurrencyName", sortable: false, width: "90px" },
        { text: "Precio Pactado", value: "Amount", sortable: false, align: "end", width: "130px" },
        { text: "Precio Matriz", value: "ConfiguredAmount", sortable: false, align: "end", width: "130px" },
        { text: "Diferencia", value: "Difference", sortable: false, align: "end", width: "130px" },
        { text: "Condición", value: "Condition", sortable: false, align: "center", width: "110px" },
      ],

      priceDetails: [],

      approvalForm: {
        HsaID: 0,
        HvsID: 0,
        TypeApprovalStatus: 1,
        Observation: "",
      },
    };
  },

  methods: {
    handlePaginationResponse(response) {
      const rows = response?.data?.data || [];
      if (rows.length === 0) return;

      const allKeys = Object.keys(rows[0]);
      const prdIdx = allKeys.indexOf("PrdCardName");
      const ignoredKeys = new Set([
        "hsaid",
        "hvsid",
        "hvald",
        "prdid",
        "date",
        "hvsdate",
        "fagname",
        "prdcardname",
        "recordtotal",
        "typeapprovalstatus",
        "typeapprovalstatusname",
        "solosinaprobacion"
      ]);
      const dynamicCols = (prdIdx !== -1 ? allKeys.slice(prdIdx + 1) : []).filter(
        (col) => !ignoredKeys.has(col.toLowerCase())
      );
      this.dynamicColumns = dynamicCols;

      dynamicCols.forEach((col) => {
        if (!this.config.model[col]) {
          this.$set(this.config.model, col, "decimal");
        }
      });

      const baseHeaders = [
        { text: "Acciones", value: "actions", sortable: false, width: "80px", align: "center" },
        { text: "Estado", value: "TypeApprovalStatus", width: "135px", align: "center", sortable: true },
        { text: "N° Prog.", value: "HvsID", width: "95px", align: "center", sortable: true },
        { text: "Fecha Cosecha", value: "Date", width: "125px", align: "center", sortable: true },
        { text: "Fundo", value: "FagName", width: "160px", sortable: true },
        { text: "Productor", value: "PrdCardName", width: "220px", sortable: true },
      ];

      const dynHeaders = dynamicCols.map((col) => {
        const isAcordado = col.includes("Precio Acordado");
        return {
          text: col,
          value: col,
          align: "end",
          sortable: false,
          width: isAcordado ? "175px" : "155px",
        };
      });

      this.config.headers = [...baseHeaders, ...dynHeaders];
    },

    formatDateDMY(val) {
      if (!val) return "-";
      if (typeof val === "string") {
        const matchYMD = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (matchYMD) return `${matchYMD[3]}/${matchYMD[2]}/${matchYMD[1]}`;
        const matchDMY = val.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
        if (matchDMY) return `${matchDMY[1]}/${matchDMY[2]}/${matchDMY[3]}`;
      }
      try {
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${day}/${m}/${y}`;
        }
      } catch (e) {}
      return "-";
    },

    formatDecimal(val) {
      const num = parseFloat(val);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    },

    formatPrice(val) {
      if (val === null || val === undefined || val === "") return "-";
      const num = parseFloat(val);
      return isNaN(num) ? val : "S/ " + num.toFixed(2);
    },

    getConfiguredColName(col) {
      return col.replace("Precio Acordado", "Precio Configurado");
    },

    isOverprice(row, col) {
      if (!col || !col.includes("Precio Acordado")) return false;
      const confCol = this.getConfiguredColName(col);
      const acordado = parseFloat(row[col]);
      const configurado = parseFloat(row[confCol]);
      if (isNaN(acordado) || isNaN(configurado)) return false;
      return acordado > configurado;
    },

    getOverpriceDiff(row, col) {
      if (!col || !col.includes("Precio Acordado")) return 0;
      const confCol = this.getConfiguredColName(col);
      const acordado = parseFloat(row[col]);
      const configurado = parseFloat(row[confCol]);
      if (isNaN(acordado) || isNaN(configurado)) return 0;
      return Math.max(0, acordado - configurado);
    },

    isUnderprice(row, col) {
      if (!col || !col.includes("Precio Acordado")) return false;
      const confCol = this.getConfiguredColName(col);
      const acordado = parseFloat(row[col]);
      const configurado = parseFloat(row[confCol]);
      if (isNaN(acordado) || isNaN(configurado)) return false;
      return acordado < configurado;
    },

    isEqualPrice(row, col) {
      if (!col || !col.includes("Precio Acordado")) return false;
      const confCol = this.getConfiguredColName(col);
      const acordado = parseFloat(row[col]);
      const configurado = parseFloat(row[confCol]);
      if (isNaN(acordado) || isNaN(configurado)) return false;
      return acordado === configurado;
    },

    computeDifference(item) {
      const amt = parseFloat(item.Amount) || 0;
      const cfg = parseFloat(item.ConfiguredAmount) || 0;
      return Math.max(0, amt - cfg);
    },

    getRowStatusName(row) {
      if (row.TypeApprovalStatusName && String(row.TypeApprovalStatusName).trim() !== "") {
        return String(row.TypeApprovalStatusName).toUpperCase();
      }
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "APROBADO";
      if (st === 2) return "RECHAZADO";
      return "PENDIENTE";
    },

    getStatusChipColor(row) {
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "green lighten-5";
      if (st === 2) return "red lighten-5";
      return "amber lighten-5";
    },

    getStatusChipClass(row) {
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "green--text text--darken-4";
      if (st === 2) return "red--text text--darken-4";
      return "amber--text text--darken-4";
    },

    getStatusChipStyle(row) {
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "border: 1px solid #86efac !important;";
      if (st === 2) return "border: 1px solid #fca5a5 !important;";
      return "border: 1px solid #fcd34d !important;";
    },

    getStatusIcon(row) {
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "mdi-check-circle-outline";
      if (st === 2) return "mdi-close-circle-outline";
      return "mdi-clock-outline";
    },

    getStatusIconColor(row) {
      const st = Number(row.TypeApprovalStatus);
      if (st === 1) return "success";
      if (st === 2) return "error";
      return "amber darken-3";
    },

    getStatusColor(status) {
      switch (Number(status)) {
        case 1:
          return "#10b981";
        case 2:
          return "#ef4444";
        default:
          return "#f59e0b";
      }
    },

    getStatusName(status) {
      switch (Number(status)) {
        case 1:
          return "Aprobado";
        case 2:
          return "Rechazado";
        default:
          return "Pendiente";
      }
    },

    async openEvaluationModal(row) {
      this.selectedRow = { ...row };
      this.approvalForm = {
        HsaID: row.HsaID || 0,
        HvsID: row.HvsID,
        TypeApprovalStatus: row.TypeApprovalStatus || 1,
        Observation: row.Observation || "",
      };

      this.dialogEvaluation = true;
      this.loadingPrices = true;
      this.priceDetails = [];

      try {
        const requestID = this.$fun?.getUserID?.() || 1;
        const res = await HarvestSchedulePriceApprovalService.getPriceListByHvs(row.HvsID, requestID);
        const data = res?.data || [];
        this.priceDetails = Array.isArray(data) ? data : (data.data || []);
      } catch (err) {
        console.error("Error al cargar detalle de precios:", err);
      } finally {
        this.loadingPrices = false;
      }
    },

    closeEvaluationModal() {
      this.dialogEvaluation = false;
      this.selectedRow = {};
      this.priceDetails = [];
      this.approvalForm = {
        HsaID: 0,
        HvsID: 0,
        TypeApprovalStatus: 1,
        Observation: "",
      };
    },

    submitApproval(targetStatus) {
      const statusText = targetStatus === 1 ? "aprobar" : "rechazar";
      const questionMsg = `¿Está seguro de ${statusText} los precios de la programación #${this.selectedRow.HvsID}?`;

      if (targetStatus === 2 && (!this.approvalForm.Observation || this.approvalForm.Observation.trim() === "")) {
        if (this.$fun && typeof this.$fun.alert === "function") {
          this.$fun.alert("Debe ingresar una observación que justifique el rechazo.", "warning");
        }
        return;
      }

      const alertFn = this.$fun && typeof this.$fun.alert === "function" ? this.$fun.alert : null;

      if (alertFn) {
        alertFn(questionMsg, "question").then((res) => {
          if (res && res.value) {
            this.executeSaveApproval(targetStatus);
          }
        });
      } else {
        this.executeSaveApproval(targetStatus);
      }
    },

    async executeSaveApproval(targetStatus) {
      this.savingApproval = true;
      try {
        const requestID = this.$fun?.getUserID?.() || 1;
        const payload = {
          HsaID: this.approvalForm.HsaID || 0,
          HvsID: this.selectedRow.HvsID,
          TypeApprovalStatus: targetStatus,
          Observation: (this.approvalForm.Observation || "").trim(),
          SecStatus: 1,
        };

        await HarvestSchedulePriceApprovalService.save(payload, requestID);

        if (this.$fun && typeof this.$fun.alert === "function") {
          this.$fun.alert(targetStatus === 1 ? "Programación aprobada." : "Programación rechazada.", "success");
        }

        this.closeEvaluationModal();
        this.refreshTableToggle = !this.refreshTableToggle;
      } catch (err) {
        console.error("Error al guardar decisión:", err);
      } finally {
        this.savingApproval = false;
      }
    },
  },
};
</script>

<style scoped>
.harvest-schedule-price-approval-view {
  background-color: #f8fafc;
  min-height: 100vh;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.border-light-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}
.bg-light {
  background-color: #f8fafc !important;
}
.border-rounded {
  border-radius: 8px !important;
}
.overprice-chip {
  border: 1px solid #fca5a5 !important;
  background-color: #fee2e2 !important;
  height: 24px !important;
  line-height: 24px !important;
}
.underprice-chip {
  border: 1px solid #86efac !important;
  background-color: #f0fdf4 !important;
  height: 24px !important;
  line-height: 24px !important;
}
.equalprice-chip {
  border: 1px solid #cbd5e1 !important;
  background-color: #f1f5f9 !important;
  height: 24px !important;
  line-height: 24px !important;
}
.config-price-chip {
  border: 1px solid #cbd5e1 !important;
  background-color: #f8fafc !important;
  height: 24px !important;
  line-height: 24px !important;
}
.status-chip {
  height: 22px !important;
  line-height: 22px !important;
  font-size: 11px !important;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
